import { css } from '@emotion/react';
import { useMemo } from 'react';

import { darkTheme } from '@/theme/dark-theme';

export interface SectionDisplayProps {
  content: string;
  speakerNotes?: string[];
  position: number;
  isCurrent: boolean;
  showDivider: boolean;
  onClick: () => void;
  fontSize?: number;
  cursorOffset?: number;
  cursorPosition?: number;
  lineWidth?: number;
  onWordClick?: (wordIndex: number) => void;
}

export function SectionDisplay({
  content,
  speakerNotes,
  position,
  isCurrent,
  showDivider,
  onClick,
  fontSize = 48,
  cursorOffset,
  cursorPosition,
  lineWidth,
  onWordClick,
}: SectionDisplayProps) {
  const wordLevelEnabled = cursorOffset !== undefined && cursorPosition !== undefined;

  const words = useMemo(() => {
    if (!wordLevelEnabled) return [];
    return content.split(/(\s+)/);
  }, [content, wordLevelEnabled]);

  function renderContent() {
    if (!wordLevelEnabled) {
      return (
        <p
          css={css`
            margin: 0;
            font-size: ${fontSize}px;
            color: ${darkTheme.text};
            white-space: pre-wrap;
            line-height: 1.4;
            ${lineWidth ? `max-width: ${lineWidth}ch; margin: 0 auto;` : ''}
          `}
        >
          {content}
        </p>
      );
    }

    let wordIndex = cursorOffset!;
    const spans: React.ReactNode[] = [];

    for (let i = 0; i < words.length; i++) {
      const token = words[i];
      // Whitespace tokens
      if (/^\s+$/.test(token)) {
        spans.push(<span key={`ws-${i}`}>{token}</span>);
        continue;
      }

      const absIndex = wordIndex;
      wordIndex++;

      const isRead = absIndex < cursorPosition!;
      const isCurWord = absIndex === cursorPosition!;
      const isUnread = absIndex > cursorPosition!;

      spans.push(
        <span
          key={absIndex}
          data-word-index={absIndex}
          {...(isCurWord ? { 'data-current-word': true } : {})}
          {...(onWordClick
            ? {
                role: 'button',
                tabIndex: 0,
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onWordClick(absIndex);
                },
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onWordClick(absIndex);
                  }
                },
              }
            : {})}
          css={css`
            ${isRead ? 'opacity: 1;' : ''}
            ${isCurWord ? `opacity: 1; border-bottom: 3px solid ${darkTheme.primary};` : ''}
            ${isUnread ? 'opacity: 0.4;' : ''}
            ${onWordClick ? 'cursor: pointer;' : ''}
            ${onWordClick
              ? `&:hover { background: ${darkTheme.primary}30; border-radius: 2px; }`
              : ''}
          `}
        >
          {token}
        </span>,
      );
    }

    return (
      <div
        data-testid="word-content"
        css={css`
          margin: 0;
          font-size: ${fontSize}px;
          color: ${darkTheme.text};
          white-space: pre-wrap;
          line-height: 1.4;
          ${lineWidth ? `max-width: ${lineWidth}ch; margin: 0 auto;` : ''}
        `}
      >
        {spans}
      </div>
    );
  }

  return (
    <>
      {showDivider && (
        <div
          data-testid="section-marker"
          css={css`
            text-align: center;
            padding: 20px 0;
            opacity: 0.6;
            font-size: 18px;
            letter-spacing: 12px;
            color: ${darkTheme.primary};
          `}
        >
          · · ·
        </div>
      )}
      <div
        data-section={position}
        data-current={isCurrent || undefined}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        css={css`
          cursor: pointer;
        `}
      >
        {speakerNotes && speakerNotes.length > 0 && (
          <div
            css={css`
              margin-bottom: 16px;
            `}
          >
            {speakerNotes.map((note, i) => (
              <div
                key={i}
                data-speaker-note
                css={css`
                  border-left: 3px solid ${darkTheme.primary};
                  background: rgba(106, 95, 193, 0.08);
                  padding: 8px 16px;
                  margin-bottom: 8px;
                  border-radius: 0 4px 4px 0;
                  font-style: italic;
                  font-size: ${fontSize * 0.5}px;
                `}
              >
                <span
                  css={css`
                    font-weight: 600;
                    font-size: 0.75em;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    margin-right: 8px;
                    color: ${darkTheme.primary};
                  `}
                >
                  Note:
                </span>
                {note}
              </div>
            ))}
          </div>
        )}
        {renderContent()}
      </div>
    </>
  );
}
